import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  styled,
  IconButton,
  Alert,
  Divider,
  alpha,
  useTheme
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { useDesigner } from '../../contexts/DesignerContext';
import { uploadCustomModel } from '../../api/files';

// Hidden file input
const HiddenInput = styled('input')({
  display: 'none',
});

interface ModelUploaderProps {
  username: string;
  projectName: string;
  onModelSelect?: (image: string) => void;
}

const ModelUploader: React.FC<ModelUploaderProps> = ({
  username,
  projectName,
  onModelSelect
}) => {
  const theme = useTheme();
  const { setModelImage } = useDesigner();
  const [uploading, setUploading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    handleFileUpload(file);
  };
  
  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setUploadError('Proszę wybrać plik obrazu (JPG, PNG, AVIF, WEBP)');
      return;
    }
    
    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);
    
    // Create a preview URL first
    const previewUrl = URL.createObjectURL(file);
    setUploadedImage(previewUrl);
    
    try {
      const result = await uploadCustomModel(file, username, projectName);
      
      if (result.status === 'success' && result.path) {
        setUploadSuccess(true);
        setUploading(false);
        
        // Clean up blob URL to avoid memory leaks
        if (previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(previewUrl);
        }
        
        // Update preview to show server path instead of blob URL
        setUploadedImage(result.path);
        
        // Use the server path rather than the local preview
        setModelImage(result.path, true);
        
        if (onModelSelect) {
          onModelSelect(result.path);
        }
      } else {
        setUploadError(result.message || 'Błąd podczas przesyłania pliku');
        setUploading(false);
      }
    } catch (error) {
      console.error('Error uploading model:', error);
      setUploadError(error instanceof Error ? error.message : 'Nieznany błąd podczas przesyłania');
      setUploading(false);
    }
  };
  
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      handleFileUpload(file);
    }
  };
  
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };
  
  const clearUploadedImage = () => {
    // Clean up blob URL if it exists
    if (uploadedImage && uploadedImage.startsWith('blob:')) {
      URL.revokeObjectURL(uploadedImage);
    }
    
    setUploadedImage(null);
    setUploadSuccess(false);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: 0, 
        mb: 4, 
        borderRadius: 3, 
        overflow: 'hidden',
        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.08)'
      }}
    >
      {/* Header Section */}
      <Box sx={{ 
        p: 3, 
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)', 
        backgroundColor: alpha(theme.palette.primary.main, 0.03),
        display: 'flex', 
        alignItems: 'center' 
      }}>
        <ImageIcon sx={{ mr: 1.5, color: theme.palette.primary.main, fontSize: 28 }} />
        <Typography variant="h5" fontWeight={600} sx={{ color: theme.palette.primary.main, letterSpacing: '-0.01em' }}>
          Zdjęcie docelowe
        </Typography>
      </Box>
      
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" gutterBottom>
          Załaduj zdjęcie modela, na którego nałożymy ubranie:
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>Obsługiwane formaty:</strong> JPG, PNG, AVIF, WEBP
        </Typography>
        
        <Box 
          sx={{
            mt: 2,
            p: 4,
            border: '2px dashed',
            borderColor: alpha(theme.palette.primary.main, 0.3),
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            minHeight: 200,
            position: 'relative',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.05)
            }
          }}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {uploadedImage ? (
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              <img 
                src={uploadedImage} 
                alt="Przesłane zdjęcie modela" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: 400,
                  objectFit: 'contain',
                  borderRadius: 8
                }}
              />
              <IconButton
                size="small"
                sx={{ 
                  position: 'absolute',
                  top: -16,
                  right: -16,
                  bgcolor: theme.palette.error.main,
                  color: 'white',
                  '&:hover': {
                    bgcolor: theme.palette.error.dark
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  clearUploadedImage();
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <UploadIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Przeciągnij i upuść zdjęcie tutaj
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  lub
                </Typography>
                <Button variant="contained" sx={{ mt: 2 }}>
                  Wybierz plik
                </Button>
              </Box>
            </>
          )}
          
          <HiddenInput
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileChange}
          />
          
          {/* Upload status */}
          {uploading && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography>Przesyłanie...</Typography>
            </Box>
          )}
        </Box>
        
        {uploadError && (
          <Alert 
            severity="error"
            sx={{ mt: 2 }}
            action={
              <IconButton
                color="inherit"
                size="small"
                onClick={() => setUploadError(null)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {uploadError}
          </Alert>
        )}
        
        {uploadSuccess && (
          <Alert 
            severity="success"
            sx={{ mt: 2 }}
          >
            Zdjęcie modela zostało pomyślnie przesłane.
          </Alert>
        )}
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="body2" color="text.secondary">
          <strong>Wskazówki:</strong>
          <ul style={{ paddingLeft: '1.5rem', margin: '0.5rem 0' }}>
            <li>Wybierz zdjęcie przedstawiające osobę w prostej pozie</li>
            <li>Upewnij się, że całe ciało jest widoczne na zdjęciu</li>
            <li>Unikaj skomplikowanych póz lub gestów</li>
            <li>Najlepiej sprawdzają się zdjęcia z neutralnym tłem</li>
          </ul>
        </Typography>
      </Box>
    </Paper>
  );
};

export default ModelUploader;
